import type { MarketResearchDemandAnalysisCalculationModelsSearchTrendsNode } from "@similarweb/pro-api-client";

import {
    type KeywordsBreakdownTableRow,
    processKeywordsBreakdownData,
} from "../../keywords-breakdown-widget/keywordsBreakdownTableDataSelector";
import { useSearchTrendsQuery } from "../shared/useSearchTrendsQuery";

import type { INewKeywordsCardData } from "./newKeywords.types";

const MAX_NEW_KEYWORDS = 5;

/**
 * A keyword is "new" when it has no period-over-period change (present now, absent in the prior period).
 * The API omits `periodVolumeChange` for such keywords, so the mapped `TotalVolumeChange` is `undefined`
 * (it is no longer defaulted to 0 upstream) — treat both undefined and 0 as "no change".
 */
const isNewKeyword = (row: KeywordsBreakdownTableRow): boolean => (row.TotalVolumeChange ?? 0) === 0;

export const selectNewKeywordsCardData = (rows: KeywordsBreakdownTableRow[]): INewKeywordsCardData => {
    const newRows = rows.filter(isNewKeyword).toSorted((left, right) => right.MonthlyVolume - left.MonthlyVolume);

    return {
        topRows: newRows.slice(0, MAX_NEW_KEYWORDS).map((row) => ({
            id: row.id,
            keyword: row.SearchTerm,
            volume: row.MonthlyVolume,
        })),
        totalNewCount: newRows.length,
    };
};

const selectNewKeywordsCard = (
    data: MarketResearchDemandAnalysisCalculationModelsSearchTrendsNode,
): INewKeywordsCardData => selectNewKeywordsCardData(processKeywordsBreakdownData(data));

export const useNewKeywordsCard = () =>
    useSearchTrendsQuery("marketResearchDemandAnalysisSearchTrendsList", selectNewKeywordsCard);
